import { z } from 'zod';

// Protocole du pont d'aperçu (plan docgen §4) : l'iframe `sandbox="allow-scripts"` n'a
// pas l'origine de l'application, donc tout message reçu est une donnée non fiable.
// On vérifie la source (`event.source === iframe.contentWindow`) puis on valide la forme.

const MAX_FRAME_HEIGHT = 200_000;

const finiteNumber = z.number().finite();

const rectSchema = z.object({
  height: finiteNumber,
  left: finiteNumber,
  top: finiteNumber,
  width: finiteNumber,
});

const idSchema = z.string().min(1).max(100);

const heightSchema = finiteNumber.nonnegative().max(MAX_FRAME_HEIGHT);

export const bridgeMessageSchema = z.discriminatedUnion('type', [
  z.object({ height: heightSchema, type: z.literal('inersio:ready') }),
  z.object({ height: heightSchema, type: z.literal('inersio:resize') }),
  z.object({
    blockId: idSchema.nullable().optional(),
    rect: rectSchema,
    sectionId: idSchema,
    type: z.literal('inersio:hover'),
  }),
  z.object({
    blockId: idSchema.nullable().optional(),
    rect: rectSchema,
    sectionId: idSchema,
    type: z.literal('inersio:select'),
  }),
  z.object({
    blockId: idSchema,
    html: z.string().max(100_000),
    sectionId: idSchema,
    type: z.literal('inersio:block-edited'),
  }),
  z.object({ blockId: idSchema, type: z.literal('inersio:edit-cancelled') }),
]);

export type BridgeMessage = z.infer<typeof bridgeMessageSchema>;
export type BridgeRect = z.infer<typeof rectSchema>;

export type BridgeInboundMessage =
  | { blockId: string; type: 'inersio:begin-edit' }
  | { blockId: string | null; sectionId: string | null; type: 'inersio:highlight' }
  | { sectionId: string; type: 'inersio:scroll-to' };

/**
 * Valide un message reçu par `window.onmessage`. Retourne null si l'émetteur n'est pas
 * l'iframe attendue ou si la charge utile ne respecte pas le protocole.
 */
export function readBridgeMessage(
  event: { data: unknown; source: unknown },
  frameWindow: unknown,
): BridgeMessage | null {
  if (!frameWindow || event.source !== frameWindow) {
    return null;
  }
  const parsed = bridgeMessageSchema.safeParse(event.data);
  return parsed.success ? parsed.data : null;
}

export function postToFrame(
  frameWindow: { postMessage: (message: unknown, targetOrigin: string) => void } | null | undefined,
  message: BridgeInboundMessage,
) {
  // L'iframe sandboxée a une origine opaque : seul '*' permet de lui écrire.
  frameWindow?.postMessage(message, '*');
}
